/**
 * Selectors over the GUMBO live-feed snapshot. Pure functions; no React.
 * Each one returns a small, frontend-friendly shape that components consume.
 *
 * Defensive against malformed snapshots: every accessor uses optional
 * chaining and returns sensible defaults. The reducer in useGameStream
 * applies in-place diffs but selectors must not throw if a field is missing.
 */
import { getInitials } from '../utils/initials';

export interface RibbonState {
  awayAbbr: string;
  homeAbbr: string;
  awayScore: number;
  homeScore: number;
  inning: number | null;
  inningOrdinal: string | null;
  inningHalf: string | null;
  isTopInning: boolean;
  outs: number;
  balls: number;
  strikes: number;
  awayTeamCode: string;
  homeTeamCode: string;
}

export interface RunnerInfo {
  id: number;
  name: string;
  initials: string;
  side: 'away' | 'home';
  jersey: string | null;
}

export interface RunnersState {
  first: RunnerInfo | null;
  second: RunnerInfo | null;
  third: RunnerInfo | null;
}

export interface FielderInfo {
  id: number;
  name: string;
  position: string;
  jersey: string | null;
  side: 'away' | 'home';
  initials: string;
}

export interface FieldersState {
  pitcher: FielderInfo | null;
  catcher: FielderInfo | null;
  first: FielderInfo | null;
  second: FielderInfo | null;
  third: FielderInfo | null;
  shortstop: FielderInfo | null;
  left: FielderInfo | null;
  center: FielderInfo | null;
  right: FielderInfo | null;
}

type AnyRecord = Record<string, unknown>;

function asRecord(v: unknown): AnyRecord {
  return (v && typeof v === 'object' ? v : {}) as AnyRecord;
}

function asNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function selectRibbon(snapshot: AnyRecord | null): RibbonState | null {
  if (!snapshot) return null;
  const gameData = asRecord(snapshot.gameData);
  const teams = asRecord(gameData.teams);
  const away = asRecord(teams.away);
  const home = asRecord(teams.home);
  const liveData = asRecord(snapshot.liveData);
  const linescore = asRecord(liveData.linescore);
  const lsTeams = asRecord(linescore.teams);
  return {
    awayAbbr: (away.abbreviation as string) ?? '?',
    homeAbbr: (home.abbreviation as string) ?? '?',
    awayScore: asNumber(asRecord(lsTeams.away).runs, 0),
    homeScore: asNumber(asRecord(lsTeams.home).runs, 0),
    inning: linescore.currentInning != null ? asNumber(linescore.currentInning) : null,
    inningOrdinal: (linescore.currentInningOrdinal as string) ?? null,
    inningHalf: (linescore.inningHalf as string) ?? null,
    isTopInning: Boolean(linescore.isTopInning),
    outs: asNumber(linescore.outs, 0),
    balls: asNumber(linescore.balls, 0),
    strikes: asNumber(linescore.strikes, 0),
    awayTeamCode: (away.teamCode as string) ?? '',
    homeTeamCode: (home.teamCode as string) ?? '',
  };
}

interface OffenseSlot {
  id?: number;
  fullName?: string;
}

function asSlot(v: unknown): OffenseSlot {
  const r = asRecord(v);
  return {
    id: r.id != null ? asNumber(r.id) : undefined,
    fullName: typeof r.fullName === 'string' ? r.fullName : undefined,
  };
}

export function selectRunners(snapshot: AnyRecord | null): RunnersState {
  const empty: RunnersState = { first: null, second: null, third: null };
  if (!snapshot) return empty;
  const linescore = asRecord(asRecord(snapshot.liveData).linescore);
  const offense = asRecord(linescore.offense);
  const isTopInning = Boolean(linescore.isTopInning);
  const battingSide: 'away' | 'home' = isTopInning ? 'away' : 'home';
  function pick(key: 'first' | 'second' | 'third'): RunnerInfo | null {
    const slot = asSlot(offense[key]);
    if (!slot.id) return null;
    const name = slot.fullName ?? `Player ${slot.id}`;
    return {
      id: slot.id,
      name,
      initials: getInitials(name),
      side: battingSide,
      jersey: findJerseyForId(snapshot, slot.id),
    };
  }
  return { first: pick('first'), second: pick('second'), third: pick('third') };
}

// Map from defense slot key → human position string + the position code we
// can look up in boxscore.players for jersey numbers.
const FIELDER_KEYS: ReadonlyArray<keyof FieldersState> = [
  'pitcher',
  'catcher',
  'first',
  'second',
  'third',
  'shortstop',
  'left',
  'center',
  'right',
];

const POSITION_LABELS: Readonly<Record<keyof FieldersState, string>> = {
  pitcher: 'P',
  catcher: 'C',
  first: '1B',
  second: '2B',
  third: '3B',
  shortstop: 'SS',
  left: 'LF',
  center: 'CF',
  right: 'RF',
};

function findJerseyAndSide(
  snapshot: AnyRecord | null,
  playerId: number,
): { jersey: string | null; side: 'away' | 'home' } {
  if (!snapshot) return { jersey: null, side: 'home' };
  const teams = asRecord(asRecord(asRecord(snapshot.liveData).boxscore).teams);
  for (const sideKey of ['away', 'home'] as const) {
    const players = asRecord(asRecord(teams[sideKey]).players);
    const player = asRecord(players[`ID${playerId}`]);
    if (Object.keys(player).length > 0) {
      const jersey = typeof player.jerseyNumber === 'string' ? player.jerseyNumber : null;
      return { jersey, side: sideKey };
    }
  }
  return { jersey: null, side: 'home' };
}

export function selectFielders(snapshot: AnyRecord | null): FieldersState {
  const empty: FieldersState = {
    pitcher: null,
    catcher: null,
    first: null,
    second: null,
    third: null,
    shortstop: null,
    left: null,
    center: null,
    right: null,
  };
  if (!snapshot) return empty;
  const defense = asRecord(asRecord(asRecord(snapshot.liveData).linescore).defense);
  const out = { ...empty };
  for (const key of FIELDER_KEYS) {
    const slot = asSlot(defense[key]);
    if (!slot.id) continue;
    const { jersey, side } = findJerseyAndSide(snapshot, slot.id);
    const name = slot.fullName ?? `Player ${slot.id}`;
    out[key] = {
      id: slot.id,
      name,
      position: POSITION_LABELS[key],
      jersey,
      side,
      initials: getInitials(name),
    };
  }
  return out;
}

export interface CurrentBatterPitcher {
  batterId: number | null;
  pitcherId: number | null;
}

export function selectCurrentBatterAndPitcher(snapshot: AnyRecord | null): CurrentBatterPitcher {
  if (!snapshot) return { batterId: null, pitcherId: null };
  const offense = asRecord(asRecord(asRecord(snapshot.liveData).linescore).offense);
  const defense = asRecord(asRecord(asRecord(snapshot.liveData).linescore).defense);
  const batter = asSlot(offense.batter);
  const pitcher = asSlot(defense.pitcher);
  return {
    batterId: batter.id ?? null,
    pitcherId: pitcher.id ?? null,
  };
}

export interface LineupHitter {
  id: number;
  name: string;
  position: string | null;
  jersey: string | null;
  battingOrder: number;
  isCurrentBatter: boolean;
  battingSummary: string | null;
}

export function selectLineup(snapshot: AnyRecord | null, side: 'away' | 'home'): LineupHitter[] {
  if (!snapshot) return [];
  const boxscore = asRecord(asRecord(snapshot.liveData).boxscore);
  const team = asRecord(asRecord(boxscore.teams)[side]);
  const battingOrderRaw = team.battingOrder;
  const battingOrder: number[] = Array.isArray(battingOrderRaw)
    ? battingOrderRaw.map((x) => asNumber(x)).filter((n) => n > 0)
    : [];
  const players = asRecord(team.players);
  const { batterId } = selectCurrentBatterAndPitcher(snapshot);
  return battingOrder.slice(0, 9).map((pid, idx) => {
    const player = asRecord(players[`ID${pid}`]);
    const person = asRecord(player.person);
    const position = asRecord(player.position);
    const stats = asRecord(asRecord(player.stats).batting);
    return {
      id: pid,
      name: (person.fullName as string) ?? `Player ${pid}`,
      position: (position.abbreviation as string) ?? null,
      jersey: typeof player.jerseyNumber === 'string' ? player.jerseyNumber : null,
      battingOrder: idx + 1,
      isCurrentBatter: pid === batterId,
      battingSummary: typeof stats.summary === 'string' ? stats.summary : null,
    };
  });
}

export interface InningLine {
  inning: number;
  away: number | null;
  home: number | null;
}

export interface LineScoreState {
  innings: InningLine[];
  away: { runs: number; hits: number; errors: number; abbr: string };
  home: { runs: number; hits: number; errors: number; abbr: string };
}

export function selectLineScore(snapshot: AnyRecord | null): LineScoreState | null {
  if (!snapshot) return null;
  const liveData = asRecord(snapshot.liveData);
  const linescore = asRecord(liveData.linescore);
  const inningsRaw = linescore.innings;
  const innings: InningLine[] = [];
  if (Array.isArray(inningsRaw)) {
    for (const i of inningsRaw) {
      const r = asRecord(i);
      const away = asRecord(r.away);
      const home = asRecord(r.home);
      innings.push({
        inning: asNumber(r.num),
        away: away.runs == null ? null : asNumber(away.runs),
        home: home.runs == null ? null : asNumber(home.runs),
      });
    }
  }
  const teamsLs = asRecord(linescore.teams);
  const ribbon = selectRibbon(snapshot);
  return {
    innings,
    away: {
      runs: asNumber(asRecord(teamsLs.away).runs, 0),
      hits: asNumber(asRecord(teamsLs.away).hits, 0),
      errors: asNumber(asRecord(teamsLs.away).errors, 0),
      abbr: ribbon?.awayAbbr ?? '?',
    },
    home: {
      runs: asNumber(asRecord(teamsLs.home).runs, 0),
      hits: asNumber(asRecord(teamsLs.home).hits, 0),
      errors: asNumber(asRecord(teamsLs.home).errors, 0),
      abbr: ribbon?.homeAbbr ?? '?',
    },
  };
}

export interface StatcastFields {
  spinRate: number | null;
  exitVelo: number | null;
  launchAngle: number | null;
  batSpeed: number | null;
  /** Plate-crossing horizontal coord (feet, catcher's view, + = right). */
  plateX: number | null;
  /** Plate-crossing vertical coord (feet from ground). */
  plateZ: number | null;
  /** Per-batter strike-zone bounds (feet from ground). */
  szTop: number | null;
  szBot: number | null;
  /** Raw description (e.g. "Ball", "Called Strike", "In play, run(s)") for plot color. */
  description: string | null;
}

export interface PitchEntry extends StatcastFields {
  /**
   * Source identity: 1-based position in the play's `playEvents`. Stable across
   * renumbering — used as a React-reconciler key and to look the event back up.
   * Can have gaps (mound visits, pickoffs, stepoffs eat indices).
   */
  index: number;
  /**
   * 1..N within the visible pitch list — assigned AFTER filtering non-pitch
   * events out. Use this for the "#" column and any user-visible "pitch N" label.
   */
  displayNumber: number;
  type: string;
  speed: number | null;
  result: string;
  isStrike: boolean;
  isBall: boolean;
  playId: string | null;
}

export type StatcastMap = Record<
  string,
  {
    spin_rate?: number | null;
    launch_speed?: number | null;
    launch_angle?: number | null;
    bat_speed?: number | null;
    plate_x?: number | null;
    plate_z?: number | null;
    sz_top?: number | null;
    sz_bot?: number | null;
    description?: string | null;
  }
>;

function pickNum(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asPitch(e: AnyRecord, index: number, statcast?: StatcastMap): PitchEntry | null {
  if (!e.isPitch) return null;
  const details = asRecord(e.details);
  const pitchData = asRecord(e.pitchData);
  const call = asRecord(details.call);
  const type = asRecord(details.type);
  const isStrike = Boolean(details.isStrike);
  const isBall = Boolean(details.isBall);
  const playId = typeof e.playId === 'string' ? (e.playId as string) : null;
  const sc = playId && statcast ? statcast[playId] : undefined;
  return {
    index,
    // Filled in by `renumberPitches` after filtering non-pitch events.
    displayNumber: 0,
    type: (type.description as string) ?? (type.code as string) ?? '?',
    speed: pitchData.startSpeed != null ? asNumber(pitchData.startSpeed) : null,
    result: (call.description as string) ?? (details.description as string) ?? '?',
    isStrike,
    isBall,
    playId,
    spinRate: pickNum(sc?.spin_rate),
    exitVelo: pickNum(sc?.launch_speed),
    launchAngle: pickNum(sc?.launch_angle),
    batSpeed: pickNum(sc?.bat_speed),
    plateX: pickNum(sc?.plate_x),
    plateZ: pickNum(sc?.plate_z),
    szTop: pickNum(sc?.sz_top),
    szBot: pickNum(sc?.sz_bot),
    description: typeof sc?.description === 'string' ? sc.description : null,
  };
}

/**
 * Build the per-at-bat pitch list with stable identity AND sequential display
 * numbering. `index` keeps the position in `playEvents` (source identity);
 * `displayNumber` is 1..N within the visible list, so mound visits and pickoffs
 * don't leave gaps in the "#" column the user sees.
 */
function eventsToPitches(events: AnyRecord[], statcast?: StatcastMap): PitchEntry[] {
  const filtered = events
    .map((e, i) => asPitch(e, i + 1, statcast))
    .filter((p): p is PitchEntry => p !== null);
  return filtered.map((p, i) => ({ ...p, displayNumber: i + 1 }));
}

export function selectPitchLog(
  snapshot: AnyRecord | null,
  statcast?: StatcastMap,
): PitchEntry[] {
  if (!snapshot) return [];
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const currentPlay = asRecord(plays.currentPlay);
  const currentEvents = Array.isArray(currentPlay.playEvents) ? (currentPlay.playEvents as AnyRecord[]) : [];
  const fromCurrent = eventsToPitches(currentEvents, statcast);
  if (fromCurrent.length > 0) return fromCurrent;

  // Fallback: most recent play that has pitches.
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  for (let i = allPlays.length - 1; i >= 0; i--) {
    const events = Array.isArray(asRecord(allPlays[i]).playEvents)
      ? (asRecord(allPlays[i]).playEvents as AnyRecord[])
      : [];
    const pitches = eventsToPitches(events, statcast);
    if (pitches.length > 0) return pitches;
  }
  return [];
}

export interface PlayHitData {
  x: number;
  y: number;
  trajectory: string | null;
  hardness: string | null;
}

export interface PlaySummary {
  atBatIndex: number;
  inning: number;
  halfInning: string;
  isTopInning: boolean;
  isComplete: boolean;
  event: string | null;
  description: string | null;
  batterName: string | null;
  batterId: number | null;
  batterJersey: string | null;
  pitcherName: string | null;
  pitcherId: number | null;
  pitcherJersey: string | null;
  pitches: PitchEntry[];
  /** Score at the end of this play. */
  awayScore: number | null;
  homeScore: number | null;
  /**
   * Coordinates of the batted ball (mapped to our 0..100 viewBox) when the
   * final playEvent in this play has hitData. Null for walks, strikeouts,
   * sacrifices without coords, and pre-pitch state.
   */
  hitData: PlayHitData | null;
}

export function selectPlaysByInning(
  snapshot: AnyRecord | null,
  inning: number,
  half: 'top' | 'bottom',
  statcast?: StatcastMap,
): PlaySummary[] {
  if (!snapshot) return [];
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  const out: PlaySummary[] = [];
  for (const p of allPlays) {
    const about = asRecord(p.about);
    if (asNumber(about.inning) !== inning) continue;
    if (((about.halfInning as string) ?? '').toLowerCase() !== half) continue;
    const result = asRecord(p.result);
    const matchup = asRecord(p.matchup);
    const batter = asSlot(matchup.batter);
    const pitcher = asSlot(matchup.pitcher);
    const events = Array.isArray(p.playEvents) ? (p.playEvents as AnyRecord[]) : [];
    const pitches = eventsToPitches(events, statcast);
    // Walk events in reverse for the latest hitData on this play. Walks /
    // strikeouts / events without coordinates simply yield null.
    let hitData: PlayHitData | null = null;
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      if (!ev) continue;
      const hit = asRecord(ev.hitData);
      const coords = asRecord(hit.coordinates);
      const mlbX = coords.coordX != null ? asNumber(coords.coordX, NaN) : NaN;
      const mlbY = coords.coordY != null ? asNumber(coords.coordY, NaN) : NaN;
      if (!Number.isFinite(mlbX) || !Number.isFinite(mlbY)) continue;
      const { x, y } = mapHit(mlbX, mlbY);
      hitData = {
        x,
        y,
        trajectory: typeof hit.trajectory === 'string' ? hit.trajectory : null,
        hardness: typeof hit.hardness === 'string' ? hit.hardness : null,
      };
      break;
    }
    out.push({
      atBatIndex: asNumber(about.atBatIndex),
      inning: asNumber(about.inning),
      halfInning: (about.halfInning as string) ?? half,
      isTopInning: Boolean(about.isTopInning),
      isComplete: Boolean(about.isComplete),
      event: (result.event as string) ?? null,
      description: (result.description as string) ?? null,
      batterName: batter.fullName ?? null,
      batterId: batter.id ?? null,
      batterJersey: batter.id ? findJerseyForId(snapshot, batter.id) : null,
      pitcherName: pitcher.fullName ?? null,
      pitcherId: pitcher.id ?? null,
      pitcherJersey: pitcher.id ? findJerseyForId(snapshot, pitcher.id) : null,
      pitches,
      awayScore: result.awayScore != null ? asNumber(result.awayScore) : null,
      homeScore: result.homeScore != null ? asNumber(result.homeScore) : null,
      hitData,
    });
  }
  return out;
}

export interface InningRange {
  maxInning: number;
  /** True if the bottom half of `maxInning` exists in the play log. */
  hasBottomOfMax: boolean;
}

export function selectInningRange(snapshot: AnyRecord | null): InningRange {
  if (!snapshot) return { maxInning: 0, hasBottomOfMax: false };
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  let maxInning = 0;
  let hasBottomOfMax = false;
  for (const p of allPlays) {
    const about = asRecord(p.about);
    const n = asNumber(about.inning);
    if (n > maxInning) {
      maxInning = n;
      hasBottomOfMax = ((about.halfInning as string) ?? '').toLowerCase() === 'bottom';
    } else if (n === maxInning && ((about.halfInning as string) ?? '').toLowerCase() === 'bottom') {
      hasBottomOfMax = true;
    }
  }
  return { maxInning, hasBottomOfMax };
}

/** Look up a play by its atBatIndex (stable across diff applies). */
export function selectPlayByAtBatIndex(
  snapshot: AnyRecord | null,
  atBatIndex: number,
): { inning: number; half: 'top' | 'bottom' } | null {
  if (!snapshot) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  for (const p of allPlays) {
    const about = asRecord(p.about);
    if (asNumber(about.atBatIndex) === atBatIndex) {
      const half = ((about.halfInning as string) ?? 'top').toLowerCase();
      return {
        inning: asNumber(about.inning),
        half: half === 'bottom' ? 'bottom' : 'top',
      };
    }
  }
  return null;
}

export interface LastHitState {
  x: number; // 0..100 in our viewBox
  y: number;
  trajectory: string | null;
  hardness: string | null;
  launchSpeed: number | null;
  launchAngle: number | null;
  distance: number | null;
  description: string | null;
  /** Stable id from the GUMBO playEvent — changes when a new hit lands. */
  hitId: string;
}

/**
 * MLB spray-chart coordinate space maps as follows in our 0..100 viewBox:
 *   ours_x = mlb_x * 0.4              (mlb 125 → 50: dead center)
 *   ours_y = mlb_y * 0.4 - 14         (mlb 210 → 70: home plate
 *                                       mlb  50 → 6:  deep center)
 * X and Y both increase toward home plate in MLB's system, matching ours.
 */
function mapHit(mlbX: number, mlbY: number): { x: number; y: number } {
  return {
    x: Math.max(2, Math.min(98, mlbX * 0.4)),
    y: Math.max(2, Math.min(85, mlbY * 0.4 - 14)),
  };
}

export function selectLastHit(snapshot: AnyRecord | null): LastHitState | null {
  if (!snapshot) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  // Walk the plays in reverse to find the most recent hitData.
  for (let i = allPlays.length - 1; i >= 0; i--) {
    const events = Array.isArray(asRecord(allPlays[i]).playEvents)
      ? (asRecord(allPlays[i]).playEvents as AnyRecord[])
      : [];
    for (let j = events.length - 1; j >= 0; j--) {
      const ev = events[j];
      if (!ev) continue;
      const hit = asRecord(ev.hitData);
      const coords = asRecord(hit.coordinates);
      const mlbX = coords.coordX != null ? asNumber(coords.coordX, NaN) : NaN;
      const mlbY = coords.coordY != null ? asNumber(coords.coordY, NaN) : NaN;
      if (!Number.isFinite(mlbX) || !Number.isFinite(mlbY)) continue;
      const { x, y } = mapHit(mlbX, mlbY);
      const playId = typeof ev.playId === 'string' ? (ev.playId as string) : `${i}-${j}`;
      return {
        x,
        y,
        trajectory: typeof hit.trajectory === 'string' ? hit.trajectory : null,
        hardness: typeof hit.hardness === 'string' ? hit.hardness : null,
        launchSpeed: hit.launchSpeed != null ? asNumber(hit.launchSpeed) : null,
        launchAngle: hit.launchAngle != null ? asNumber(hit.launchAngle) : null,
        distance: hit.totalDistance != null ? asNumber(hit.totalDistance) : null,
        description: typeof (asRecord(ev.details).description) === 'string'
          ? (asRecord(ev.details).description as string)
          : null,
        hitId: playId,
      };
    }
  }
  return null;
}

export type RunnerBase = '1B' | '2B' | '3B' | 'home' | 'score';

export interface RunnerMovement {
  id: number;
  name: string;
  initials: string;
  jersey: string | null;
  /** Origin base or null when the runner is the batter (started at home plate). */
  start: RunnerBase | null;
  /** Destination — null means the runner didn't actually advance (was put out before). */
  end: RunnerBase | null;
  /** True for outs recorded on this runner; outBase holds where the out was made. */
  isOut: boolean;
  outBase: RunnerBase | null;
  isScoringEvent: boolean;
}

function asRunnerBase(s: unknown): RunnerBase | null {
  if (typeof s !== 'string') return null;
  if (s === '1B' || s === '2B' || s === '3B' || s === 'home' || s === 'score') {
    return s;
  }
  return null;
}

function readRunnerMovements(snapshot: AnyRecord, play: AnyRecord): RunnerMovement[] {
  const runnersRaw = Array.isArray(play.runners) ? (play.runners as AnyRecord[]) : [];
  const out: RunnerMovement[] = [];
  for (const r of runnersRaw) {
    const movement = asRecord(r.movement);
    const details = asRecord(r.details);
    const runner = asRecord(details.runner);
    const id = runner.id != null ? asNumber(runner.id) : null;
    if (!id) continue;
    const name = typeof runner.fullName === 'string' ? (runner.fullName as string) : `Player ${id}`;
    out.push({
      id,
      name,
      initials: getInitials(name),
      jersey: findJerseyForId(snapshot, id),
      start: asRunnerBase(movement.start),
      end: asRunnerBase(movement.end),
      isOut: Boolean(movement.isOut),
      outBase: asRunnerBase(movement.outBase),
      isScoringEvent: Boolean(details.isScoringEvent),
    });
  }
  return out;
}

export interface AtBatHit {
  x: number;
  y: number;
  /** Pitch number (1..N) within the at-bat, matching PitchEntry.displayNumber. */
  displayNumber: number;
  /** True for the contact play event that ended the at-bat; false for fouls. */
  isInPlay: boolean;
  trajectory: string | null;
  hardness: string | null;
  launchSpeed: number | null;
  launchAngle: number | null;
  distance: number | null;
  /** Stable id from the GUMBO playEvent — survives React reconciliation. */
  hitId: string;
}

export interface AtBatContextBatter {
  id: number;
  name: string;
  jersey: string | null;
}

export interface AtBatContextPitcher {
  id: number;
  name: string;
  jersey: string | null;
}

export interface AtBatContext {
  atBatIndex: number;
  inning: number;
  half: 'top' | 'bottom';
  isComplete: boolean;
  event: string | null;
  description: string | null;
  awayScore: number | null;
  homeScore: number | null;
  batter: AtBatContextBatter | null;
  pitcher: AtBatContextPitcher | null;
  pitches: PitchEntry[];
  /** All hitData coords for the at-bat (in-play + fouls), tagged by pitch number. */
  hits: AtBatHit[];
  /** Runner movements + outs from this at-bat — drives AtBatResultArrows + AtBatResultBanner. */
  runners: RunnerMovement[];
}

function findJerseyForId(snapshot: AnyRecord | null, playerId: number): string | null {
  if (!snapshot) return null;
  const teams = asRecord(asRecord(asRecord(snapshot.liveData).boxscore).teams);
  for (const sideKey of ['away', 'home'] as const) {
    const players = asRecord(asRecord(teams[sideKey]).players);
    const player = asRecord(players[`ID${playerId}`]);
    if (Object.keys(player).length > 0 && typeof player.jerseyNumber === 'string') {
      return player.jerseyNumber as string;
    }
  }
  return null;
}

/**
 * Resolve a single at-bat into the canonical object every consumer
 * (PitchLog, FieldDiamond ping, AtBatResultBanner + AtBatResultArrows, LineScore highlight) needs.
 * Single source of truth — no consumer derives this independently, so they
 * can't desynchronize on a malformed snapshot.
 */
export function selectAtBatContext(
  snapshot: AnyRecord | null,
  atBatIndex: number | null,
  statcast?: StatcastMap,
): AtBatContext | null {
  if (!snapshot || atBatIndex == null) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  const currentPlay = asRecord(plays.currentPlay);

  let play: AnyRecord | null = null;
  for (const p of allPlays) {
    const about = asRecord(p.about);
    if (asNumber(about.atBatIndex) === atBatIndex) {
      play = p;
      break;
    }
  }
  // Fall back to currentPlay if the index matches (it isn't always mirrored
  // into allPlays mid-at-bat).
  if (!play) {
    const about = asRecord(currentPlay.about);
    if (asNumber(about.atBatIndex) === atBatIndex && Object.keys(currentPlay).length > 0) {
      play = currentPlay;
    }
  }
  if (!play) return null;

  const about = asRecord(play.about);
  const halfRaw = ((about.halfInning as string) ?? '').toLowerCase();
  // Strict guard — never silently coerce. If GUMBO ships an unexpected half
  // value the at-bat is unresolvable and consumers should render nothing.
  if (halfRaw !== 'top' && halfRaw !== 'bottom') return null;
  const half: 'top' | 'bottom' = halfRaw;
  const inning = asNumber(about.inning);

  const result = asRecord(play.result);
  const matchup = asRecord(play.matchup);
  const batterSlot = asSlot(matchup.batter);
  const pitcherSlot = asSlot(matchup.pitcher);
  const events = Array.isArray(play.playEvents) ? (play.playEvents as AnyRecord[]) : [];
  const pitches = eventsToPitches(events, statcast);

  // Walk events once, building hits[] in the same order as the user sees them.
  // Display numbers are taken from the renumbered pitch list — fouls map onto
  // the pitch event they were called on; non-pitch events (mound visits) have
  // no displayNumber, so they're skipped naturally because they carry no hitData.
  const eventToDisplayNumber = new Map<number, number>();
  for (const pitch of pitches) {
    // pitch.index is the 1-based position in playEvents. Convert to 0-based.
    eventToDisplayNumber.set(pitch.index - 1, pitch.displayNumber);
  }
  const hits: AtBatHit[] = [];
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (!ev) continue;
    const hit = asRecord(ev.hitData);
    const coords = asRecord(hit.coordinates);
    const mlbX = coords.coordX != null ? asNumber(coords.coordX, NaN) : NaN;
    const mlbY = coords.coordY != null ? asNumber(coords.coordY, NaN) : NaN;
    if (!Number.isFinite(mlbX) || !Number.isFinite(mlbY)) continue;
    const details = asRecord(ev.details);
    const isInPlay = Boolean(details.isInPlay);
    const playId = typeof ev.playId === 'string' ? (ev.playId as string) : `${atBatIndex}-${i}`;
    const { x, y } = mapHit(mlbX, mlbY);
    hits.push({
      x,
      y,
      displayNumber: eventToDisplayNumber.get(i) ?? 0,
      isInPlay,
      trajectory: typeof hit.trajectory === 'string' ? hit.trajectory : null,
      hardness: typeof hit.hardness === 'string' ? hit.hardness : null,
      launchSpeed: hit.launchSpeed != null ? asNumber(hit.launchSpeed) : null,
      launchAngle: hit.launchAngle != null ? asNumber(hit.launchAngle) : null,
      distance: hit.totalDistance != null ? asNumber(hit.totalDistance) : null,
      hitId: playId,
    });
  }

  return {
    atBatIndex,
    inning,
    half,
    isComplete: Boolean(about.isComplete),
    event: typeof result.event === 'string' ? (result.event as string) : null,
    description: typeof result.description === 'string' ? (result.description as string) : null,
    awayScore: result.awayScore != null ? asNumber(result.awayScore) : null,
    homeScore: result.homeScore != null ? asNumber(result.homeScore) : null,
    batter: batterSlot.id
      ? {
          id: batterSlot.id,
          name: batterSlot.fullName ?? `Player ${batterSlot.id}`,
          jersey: findJerseyForId(snapshot, batterSlot.id),
        }
      : null,
    pitcher: pitcherSlot.id
      ? {
          id: pitcherSlot.id,
          name: pitcherSlot.fullName ?? `Player ${pitcherSlot.id}`,
          jersey: findJerseyForId(snapshot, pitcherSlot.id),
        }
      : null,
    pitches,
    hits,
    runners: readRunnerMovements(snapshot, play),
  };
}

/**
 * Highest atBatIndex visible in the snapshot. Used by GameView to track
 * "show latest" — when `selectedAtBatIndex` is null, the latest index is used.
 * Falls back to currentPlay.about.atBatIndex when allPlays is empty (Opening Day
 * fixtures, fresh-game state).
 */
export function selectLatestAtBatIndex(snapshot: AnyRecord | null): number | null {
  if (!snapshot) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  let max = -1;
  for (const p of allPlays) {
    const about = asRecord(p.about);
    const idx = asNumber(about.atBatIndex, -1);
    if (idx > max) max = idx;
  }
  if (max >= 0) return max;
  const currentPlay = asRecord(plays.currentPlay);
  const about = asRecord(currentPlay.about);
  return about.atBatIndex != null ? asNumber(about.atBatIndex) : null;
}

/** Get the atBatIndex of the currentPlay (used when clicking a pitch result). */
export function selectCurrentAtBatIndex(snapshot: AnyRecord | null): number | null {
  if (!snapshot) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const currentPlay = asRecord(plays.currentPlay);
  const about = asRecord(currentPlay.about);
  return about.atBatIndex != null ? asNumber(about.atBatIndex) : null;
}

export interface CurrentPitcher {
  id: number;
  name: string;
  jersey: string | null;
  side: 'away' | 'home';
  pitchesThrown: number;
  pitchesThisAtBat: number;
  inningsPitched: string;
}

export function selectCurrentPitcher(snapshot: AnyRecord | null): CurrentPitcher | null {
  if (!snapshot) return null;
  const { pitcherId } = selectCurrentBatterAndPitcher(snapshot);
  if (!pitcherId) return null;
  const liveData = asRecord(snapshot.liveData);
  const boxscore = asRecord(liveData.boxscore);
  const teams = asRecord(boxscore.teams);
  for (const sideKey of ['away', 'home'] as const) {
    const players = asRecord(asRecord(teams[sideKey]).players);
    const player = asRecord(players[`ID${pitcherId}`]);
    if (Object.keys(player).length === 0) continue;
    const person = asRecord(player.person);
    const pitching = asRecord(asRecord(player.stats).pitching);
    const currentPlay = asRecord(asRecord(liveData.plays).currentPlay);
    const playEventsRaw = currentPlay.playEvents;
    const playEvents: AnyRecord[] = Array.isArray(playEventsRaw) ? (playEventsRaw as AnyRecord[]) : [];
    const pitchesThisAtBat = playEvents.filter((e) => Boolean(e.isPitch)).length;
    return {
      id: pitcherId,
      name: (person.fullName as string) ?? `Player ${pitcherId}`,
      jersey: typeof player.jerseyNumber === 'string' ? player.jerseyNumber : null,
      side: sideKey,
      pitchesThrown: asNumber(pitching.pitchesThrown, 0),
      pitchesThisAtBat,
      inningsPitched: (pitching.inningsPitched as string) ?? '0.0',
    };
  }
  return null;
}

export interface PitchTypeCount {
  /** Human label ("Four-Seam Fastball") falling back to the code, then "?". */
  type: string;
  count: number;
}

export interface PitcherLine {
  id: number;
  name: string;
  jersey: string | null;
  inningsPitched: string;
  /** Total pitches thrown this game. */
  pitches: number;
  strikes: number;
  balls: number;
  /** strikes / pitches in 0..1, or null when no pitches thrown yet. */
  strikePct: number | null;
  strikeOuts: number;
  baseOnBalls: number;
  hits: number;
  runs: number;
  earnedRuns: number;
  battersFaced: number;
  /** Decision note, e.g. "(W, 2-0)" — null when absent. */
  note: string | null;
  /** Pitch-type mix for this game, sorted descending by count. Empty when
   *  GUMBO carries no per-pitch type detail. */
  pitchMix: PitchTypeCount[];
}

/**
 * Aggregate each pitcher's pitch-type counts from the play-by-play. Boxscore
 * stats give per-game totals but no type breakdown, so we walk `allPlays` once
 * and bucket every pitch event by its matchup pitcher id. Pitchers with no
 * type detail simply get an empty map.
 */
function buildPitchMix(snapshot: AnyRecord): Map<number, Map<string, number>> {
  const byPitcher = new Map<number, Map<string, number>>();
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const allPlays = Array.isArray(plays.allPlays) ? (plays.allPlays as AnyRecord[]) : [];
  for (const p of allPlays) {
    const pitcher = asSlot(asRecord(p.matchup).pitcher);
    if (!pitcher.id) continue;
    const events = Array.isArray(p.playEvents) ? (p.playEvents as AnyRecord[]) : [];
    for (const e of events) {
      if (!e || !e.isPitch) continue;
      const type = asRecord(asRecord(e.details).type);
      const label =
        (typeof type.description === 'string' && type.description) ||
        (typeof type.code === 'string' && type.code) ||
        '?';
      let counts = byPitcher.get(pitcher.id);
      if (!counts) {
        counts = new Map<string, number>();
        byPitcher.set(pitcher.id, counts);
      }
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
  }
  return byPitcher;
}

/**
 * Per-pitcher game lines for one side, in the order they appeared (the
 * boxscore `pitchers` array). Totals come from boxscore pitching stats
 * (MLB.com-parity: P / strikes / balls / strike% / IP / K / BB); the pitch-type
 * mix is aggregated from the play-by-play. Defensive: returns [] on a malformed
 * snapshot and omits pitchers with no boxscore entry.
 */
export function selectPitcherLines(snapshot: AnyRecord | null, side: 'away' | 'home'): PitcherLine[] {
  if (!snapshot) return [];
  const boxscore = asRecord(asRecord(snapshot.liveData).boxscore);
  const team = asRecord(asRecord(boxscore.teams)[side]);
  const players = asRecord(team.players);
  const order = Array.isArray(team.pitchers)
    ? (team.pitchers as unknown[]).map((x) => asNumber(x)).filter((n) => n > 0)
    : [];
  const pitchMix = buildPitchMix(snapshot);
  const lines: PitcherLine[] = [];
  for (const pid of order) {
    const player = asRecord(players[`ID${pid}`]);
    if (Object.keys(player).length === 0) continue;
    const person = asRecord(player.person);
    const pitching = asRecord(asRecord(player.stats).pitching);
    const pitches = asNumber(pitching.pitchesThrown ?? pitching.numberOfPitches, 0);
    const strikes = asNumber(pitching.strikes, 0);
    const balls = asNumber(pitching.balls, 0);
    const mix = pitchMix.get(pid);
    const pitchMixList: PitchTypeCount[] = mix
      ? Array.from(mix.entries())
          .map(([type, count]) => ({ type, count }))
          .sort((a, b) => b.count - a.count)
      : [];
    lines.push({
      id: pid,
      name: (person.fullName as string) ?? `Player ${pid}`,
      jersey: typeof player.jerseyNumber === 'string' ? player.jerseyNumber : null,
      inningsPitched: (pitching.inningsPitched as string) ?? '0.0',
      pitches,
      strikes,
      balls,
      strikePct: pitches > 0 ? strikes / pitches : null,
      strikeOuts: asNumber(pitching.strikeOuts, 0),
      baseOnBalls: asNumber(pitching.baseOnBalls, 0),
      hits: asNumber(pitching.hits, 0),
      runs: asNumber(pitching.runs, 0),
      earnedRuns: asNumber(pitching.earnedRuns, 0),
      battersFaced: asNumber(pitching.battersFaced, 0),
      note: typeof pitching.note === 'string' ? pitching.note : null,
      pitchMix: pitchMixList,
    });
  }
  return lines;
}

export interface VenueInfo {
  name: string;
  city: string | null;
  stateAbbrev: string | null;
  capacity: number | null;
  roofType: string | null;
  attendance: number | null;
  occupancy: number | null;
  weather: {
    condition: string | null;
    temp: string | null;
    wind: string | null;
  } | null;
  firstPitchIso: string | null;
  timezoneId: string | null;
}

export function selectVenueInfo(snapshot: AnyRecord | null): VenueInfo | null {
  if (!snapshot) return null;
  const gameData = asRecord(snapshot.gameData);
  const venue = asRecord(gameData.venue);
  const name = typeof venue.name === 'string' ? (venue.name as string) : null;
  if (!name) return null;
  const location = asRecord(venue.location);
  const fieldInfo = asRecord(venue.fieldInfo);
  const tz = asRecord(venue.timeZone);
  const gameInfo = asRecord(gameData.gameInfo);
  const weatherRaw = asRecord(gameData.weather);
  const datetime = asRecord(gameData.datetime);

  const capacity =
    fieldInfo.capacity != null ? asNumber(fieldInfo.capacity, NaN) : NaN;
  const attendance =
    gameInfo.attendance != null ? asNumber(gameInfo.attendance, NaN) : NaN;
  const occupancy =
    Number.isFinite(capacity) && Number.isFinite(attendance) && capacity > 0
      ? attendance / capacity
      : null;

  const hasWeather =
    typeof weatherRaw.condition === 'string' ||
    typeof weatherRaw.temp === 'string' ||
    typeof weatherRaw.wind === 'string';

  const firstPitchIso =
    typeof gameInfo.firstPitch === 'string'
      ? (gameInfo.firstPitch as string)
      : typeof datetime.dateTime === 'string'
        ? (datetime.dateTime as string)
        : null;

  return {
    name,
    city: typeof location.city === 'string' ? (location.city as string) : null,
    stateAbbrev:
      typeof location.stateAbbrev === 'string'
        ? (location.stateAbbrev as string)
        : null,
    capacity: Number.isFinite(capacity) ? capacity : null,
    roofType:
      typeof fieldInfo.roofType === 'string' ? (fieldInfo.roofType as string) : null,
    attendance: Number.isFinite(attendance) ? attendance : null,
    occupancy,
    weather: hasWeather
      ? {
          condition:
            typeof weatherRaw.condition === 'string'
              ? (weatherRaw.condition as string)
              : null,
          temp:
            typeof weatherRaw.temp === 'string' ? (weatherRaw.temp as string) : null,
          wind:
            typeof weatherRaw.wind === 'string' ? (weatherRaw.wind as string) : null,
        }
      : null,
    firstPitchIso,
    timezoneId: typeof tz.id === 'string' ? (tz.id as string) : null,
  };
}

/**
 * Source-of-truth: every player id the user can click from the game view.
 * Lineup hitters (both sides) + 9 fielders + up to 3 runners + currentPitcher
 * + currentBatter + currentPlay's batter/pitcher (clickable via PitchLog
 * result). Deduped.
 *
 * Used by `usePrefetchPlayers` on GameView mount so click→modal feels
 * instant. If the click surface set ever grows (bench, subs, etc), update
 * this selector — the prefetch hook follows automatically.
 */
export function selectClickablePlayerIds(snapshot: AnyRecord | null): number[] {
  if (!snapshot) return [];
  const ids = new Set<number>();
  for (const side of ['away', 'home'] as const) {
    for (const h of selectLineup(snapshot, side)) {
      if (Number.isFinite(h.id) && h.id > 0) ids.add(h.id);
    }
  }
  const fielders = selectFielders(snapshot);
  for (const k of [
    'pitcher',
    'catcher',
    'first',
    'second',
    'third',
    'shortstop',
    'left',
    'center',
    'right',
  ] as const) {
    const f = fielders[k];
    if (f && Number.isFinite(f.id) && f.id > 0) ids.add(f.id);
  }
  const runners = selectRunners(snapshot);
  for (const base of ['first', 'second', 'third'] as const) {
    const r = runners[base];
    if (r && Number.isFinite(r.id) && r.id > 0) ids.add(r.id);
  }
  const cp = selectCurrentPitcher(snapshot);
  if (cp && Number.isFinite(cp.id) && cp.id > 0) ids.add(cp.id);
  const { batterId, pitcherId } = selectCurrentBatterAndPitcher(snapshot);
  if (batterId && batterId > 0) ids.add(batterId);
  if (pitcherId && pitcherId > 0) ids.add(pitcherId);
  return Array.from(ids);
}

/**
 * Every player id present in either team's boxscore — starters + bench +
 * bullpen. Distinct from `selectClickablePlayerIds` (currently on the field).
 * Used by `usePrefetchPlayers` to warm the modal cache for the FULL roster
 * (so navigating to /rosters and clicking any name is also instant).
 */
export function selectAllRosteredIds(snapshot: AnyRecord | null): number[] {
  if (!snapshot) return [];
  const ids = new Set<number>();
  const teams = asRecord(asRecord(asRecord(snapshot.liveData).boxscore).teams);
  for (const sideKey of ['away', 'home'] as const) {
    const players = asRecord(asRecord(teams[sideKey]).players);
    for (const key of Object.keys(players)) {
      // Player keys are formatted "ID12345" — strip the prefix.
      if (!key.startsWith('ID')) continue;
      const idNum = Number(key.slice(2));
      if (Number.isFinite(idNum) && idNum > 0) ids.add(idNum);
    }
  }
  return Array.from(ids);
}

/**
 * Inning-modal subset of the same idea: batter + pitcher of every play in
 * the visible half-inning. Hook into `usePrefetchPlayers` so clicks inside
 * the inning modal also feel instant.
 */
export function selectInningClickableIds(
  snapshot: AnyRecord | null,
  inning: number,
  half: 'top' | 'bottom',
): number[] {
  if (!snapshot) return [];
  const ids = new Set<number>();
  for (const p of selectPlaysByInning(snapshot, inning, half)) {
    if (p.batterId && p.batterId > 0) ids.add(p.batterId);
    if (p.pitcherId && p.pitcherId > 0) ids.add(p.pitcherId);
  }
  return Array.from(ids);
}

export interface BatterOnFieldInfo {
  id: number;
  name: string;
  jersey: string | null;
  initials: string;
  /** Player's default batting hand (L/R/S). For switch hitters S is the registered side. */
  defaultSide: 'L' | 'R' | 'S';
  /** Active side at this plate appearance — flips for switch hitters based on pitcher hand. */
  activeSide: 'L' | 'R';
  /** Which team batting — used to color the badge. */
  side: 'away' | 'home';
}

/**
 * The batter currently at the plate, with handedness for which side of home
 * plate the on-field batter badge should be rendered on. Reads `matchup.batSide.code`
 * (active side, GUMBO already flips it for switch hitters based on pitcher).
 */
export function selectBatterOnField(snapshot: AnyRecord | null): BatterOnFieldInfo | null {
  if (!snapshot) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const currentPlay = asRecord(plays.currentPlay);
  const matchup = asRecord(currentPlay.matchup);
  const batter = asSlot(matchup.batter);
  if (!batter.id || !batter.fullName) return null;
  const batSide = asRecord(matchup.batSide);
  const activeCode = (typeof batSide.code === 'string' ? batSide.code : 'R').toUpperCase();
  const activeSide: 'L' | 'R' = activeCode === 'L' ? 'L' : 'R';

  // Default side from gameData.players.IDxxx.batSide.code for the indicator.
  const gameData = asRecord(snapshot.gameData);
  const players = asRecord(gameData.players);
  const playerEntry = asRecord(players[`ID${batter.id}`]);
  const playerBatSide = asRecord(playerEntry.batSide);
  const defaultCode =
    typeof playerBatSide.code === 'string' ? playerBatSide.code.toUpperCase() : activeCode;
  const defaultSide: 'L' | 'R' | 'S' =
    defaultCode === 'L' || defaultCode === 'S' ? (defaultCode as 'L' | 'S') : 'R';

  // Find which side (away/home) the batter is on via boxscore.
  const boxscore = asRecord(asRecord(snapshot.liveData).boxscore);
  const teams = asRecord(boxscore.teams);
  let teamSide: 'away' | 'home' = 'away';
  for (const sideKey of ['away', 'home'] as const) {
    const playersOnSide = asRecord(asRecord(teams[sideKey]).players);
    if (playersOnSide[`ID${batter.id}`]) {
      teamSide = sideKey;
      break;
    }
  }
  const boxPlayers = asRecord(asRecord(teams[teamSide]).players);
  const playerBox = asRecord(boxPlayers[`ID${batter.id}`]);

  return {
    id: batter.id,
    name: batter.fullName,
    jersey: typeof playerBox.jerseyNumber === 'string' ? playerBox.jerseyNumber : null,
    initials: getInitials(batter.fullName),
    defaultSide,
    activeSide,
    side: teamSide,
  };
}

export interface AtBatResult {
  event: string;
  description: string | null;
}

/**
 * The just-finished at-bat's result (event + description) when the current
 * play is complete. Returns null during an in-progress at-bat. Used by the
 * AtBatResultBadge to replace the STATCAST pill in PitchLog's header.
 */
export function selectCurrentAtBatResult(snapshot: AnyRecord | null): AtBatResult | null {
  if (!snapshot) return null;
  const plays = asRecord(asRecord(snapshot.liveData).plays);
  const currentPlay = asRecord(plays.currentPlay);
  const about = asRecord(currentPlay.about);
  if (!about.isComplete) return null;
  const result = asRecord(currentPlay.result);
  const event = typeof result.event === 'string' ? result.event : null;
  if (!event) return null;
  return {
    event,
    description: typeof result.description === 'string' ? result.description : null,
  };
}

export interface LastAtBatResult {
  atBatIndex: number;
  event: string;
  description: string | null;
  runners: RunnerMovement[];
}

/**
 * The most-recent pitcher per side. GameView keeps a ref of the prior value
 * and renders the "Now pitching: X" banner when it changes.
 */
export function selectMostRecentPitcherIds(
  snapshot: AnyRecord | null,
): { away: number | null; home: number | null } {
  const out: { away: number | null; home: number | null } = { away: null, home: null };
  if (!snapshot) return out;
  const boxscore = asRecord(asRecord(snapshot.liveData).boxscore);
  const teams = asRecord(boxscore.teams);
  for (const sideKey of ['away', 'home'] as const) {
    const sideRec = asRecord(teams[sideKey]);
    const pitchers = Array.isArray(sideRec.pitchers) ? (sideRec.pitchers as number[]) : [];
    if (pitchers.length > 0) {
      const last = pitchers[pitchers.length - 1];
      out[sideKey] = typeof last === 'number' ? last : null;
    }
  }
  return out;
}
